import { normalizeUrl } from '../chrome/normalizeUrl';
import { dbLog } from './db';
import { isNotEmpty } from '../shared/utils';
import { shouldIgnore } from './urlNormalizationPrefs';
import { accessTimeAdjacentBatch, ensurePageTracked, pageOpenedFrom, updateFromBrowser } from './pages';
import { logger } from '../utils/logger';
import { now } from 'lodash';
import { applyTitlePlugins, isChromeGenericTitle } from '../titlePlugins';

// Local cache of recent tabs for computing adjacency
// Mirrors windowsSlice.recentTabs logic
let recentTabs: { [key: string]: number } = {};

// Title overrides provided by content scripts (normalizedUrl → title).
// Takes precedence over the Chrome-reported tab title so that richer titles
// sent by a content script (e.g. Gemini conversation names) are not clobbered
// by subsequent handleTabUpdated events that carry only the generic page title.
//
// TTL-based to prevent unbounded growth: entries expire after 30 minutes.
const TITLE_OVERRIDE_TTL_MS = 30 * 60 * 1000;
const MAX_TITLE_LENGTH = 500;
const titleOverrideCache = new Map<string, { title: string; expiresAt: number }>();

/** Strip control characters and enforce max length on a content-script-supplied title. */
function sanitizeTitle(raw: string): string {
  // Replace control characters (U+0000–U+001F, U+007F) with a space, then collapse
  // runs of whitespace. This guards against null bytes and hidden formatting chars
  // injected by a compromised page.
  // eslint-disable-next-line no-control-regex
  return raw.replace(/[\x00-\x1F\x7F]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, MAX_TITLE_LENGTH);
}

/**
 * Store a content-script-provided title override and use it in preference to
 * the Chrome-reported title for subsequent tab events on this URL.
 */
export function setTitleOverride(normalizedUrl: string, title: string): void {
  const sanitized = sanitizeTitle(title);
  if (!sanitized) return;
  titleOverrideCache.set(normalizedUrl, { title: sanitized, expiresAt: Date.now() + TITLE_OVERRIDE_TTL_MS });
}

function effectiveTitle(normalizedUrl: string, chromeTitle: string | undefined): string | undefined {
  const entry = titleOverrideCache.get(normalizedUrl);
  if (entry) {
    if (Date.now() < entry.expiresAt) return entry.title;
    titleOverrideCache.delete(normalizedUrl); // expired
  }
  if (!chromeTitle) return chromeTitle;
  // If Chrome is reporting a known generic placeholder title, return undefined so that
  // ensurePageTracked preserves any richer title already stored in the DB rather than
  // overwriting it.  This matters after a service worker restart, when titleOverrideCache
  // is empty and syncInitialState would otherwise clobber the real conversation title.
  if (isChromeGenericTitle(normalizedUrl, chromeTitle)) return undefined;
  return applyTitlePlugins(normalizedUrl, chromeTitle);
}

function trimRecentTabs() {
  const retainSeconds = 300;
  const cutoff = now() - retainSeconds * 1000;
  for (const url in recentTabs) {
    if (recentTabs[url] < cutoff) {
      delete recentTabs[url];
    }
  }
}

function updateRecentTabs(url: string) {
    trimRecentTabs();
    if (isNotEmpty(url)) {
        recentTabs[url] = now();
    }
}

async function updateOpenerLinks(chromeTab: chrome.tabs.Tab) {
  if (chromeTab && chromeTab.url && chromeTab.openerTabId) {
    const opened = normalizeUrl(chromeTab.url);
    try {
        const openerTab = await chrome.tabs.get(chromeTab.openerTabId);
        if (openerTab && openerTab.url) {
            const opener = normalizeUrl(openerTab.url);
            if (
                opener.normalized == 'chrome://extensions/' ||
                opened.normalized == 'chrome://extensions/'
            ) {
                return;
            }

            logger.debug('Tab opened from', opener, '->', opened);
            await pageOpenedFrom(opener.normalized, opened.normalized);
        }
    } catch(e) {
        // Opener tab might be closed
        console.debug("Could not get opener tab", e);
    }
  }
}

async function updateAdjacentAccessLinks(chromeTab: chrome.tabs.Tab) {
  if (!chromeTab?.url) return;
  try {
    const focalNorm = normalizeUrl(chromeTab.url);
    // Collect all (other, focal) pairs first, then write in one transaction instead of N.
    const pairs: [string, string][] = [];
    for (const otherUrl of Object.keys(recentTabs)) {
      const otherNorm = normalizeUrl(otherUrl);
      if (otherNorm.normalized !== focalNorm.normalized) {
        pairs.push([otherNorm.normalized, focalNorm.normalized]);
      }
    }
    if (pairs.length > 0) {
      await accessTimeAdjacentBatch(pairs);
    }
  } catch (e) {
    logger.error("updateAdjacentAccessLinks error", e);
  }
}

// Flag to ignore events during initial sync
let isInitializing = false;

// Tab IDs that existed when the service worker started. Their first onActivated event is
// a browser-initiated restore, not a user action — skip the access time update for it.
const restoredTabIds = new Set<number>();

// Tab IDs created after the service worker started. Used to record an access time on the
// first URL load even when the tab opens in the background (e.g. option-click / middle-click).
const newTabIds = new Set<number>();

export function handleTabCreated(chromeTab: chrome.tabs.Tab) {
    if (isInitializing) return;
    if (chromeTab.id != null) {
        newTabIds.add(chromeTab.id);
    }
}

export async function handleTabActivated(activeInfo: chrome.tabs.TabActiveInfo) {
    if (isInitializing) return;
    // If this tab was newly created and is now being activated, the active flag in
    // handleTabUpdated will record the access time — remove from newTabIds to avoid
    // double-counting a second entry in the same session.
    newTabIds.delete(activeInfo.tabId);
    try {
        const isRestored = restoredTabIds.has(activeInfo.tabId);
        restoredTabIds.delete(activeInfo.tabId);

        const chromeTab = await chrome.tabs.get(activeInfo.tabId);
        if (isNotEmpty(chromeTab.url) && !shouldIgnore(chromeTab.url!)) {
            const nurl = normalizeUrl(chromeTab.url!);
            updateRecentTabs(nurl.normalized);

            await ensurePageTracked(
                nurl,
                !isRestored,
                effectiveTitle(nurl.normalized, chromeTab.title),
                chromeTab.favIconUrl,
                chromeTab.windowId
            );
            // Adjacent-access links reflect which pages are browsed in the same session.
            // Computed on activation (actual focus switch) rather than on every onUpdated
            // event (which fires for title changes, load progress, favicon updates, etc.)
            // to avoid a flood of DB writes per navigation.
            await updateAdjacentAccessLinks(chromeTab);
        }
    } catch (e) {
        logger.warn("handleTabActivated error", e);
    }
}

export async function handleTabUpdated(
  tabId: number,
  changeInfo: chrome.tabs.TabChangeInfo,
  chromeTab: chrome.tabs.Tab
) {
    // dbLog('updated', chromeTab);
    if (isInitializing) return;

    // Chrome fires onUpdated with changeInfo.discarded=true when memory-saver unloads a tab.
    // The URL and title are preserved by Chrome, but some versions briefly report a transient
    // URL (e.g. about:blank) in the tab object. Skip this event entirely: the page is already
    // tracked, no DB update is needed, and we must not clobber the stored URL/title.
    if (changeInfo.discarded) return;

    try {
        if (isNotEmpty(chromeTab.url) && !shouldIgnore(chromeTab.url!)) {
            const nurl = normalizeUrl(chromeTab.url!);
            updateRecentTabs(nurl.normalized);

            // isNewTab: this tab was created in this SW session and this is its first URL load.
            // Opening a link in a new background tab (option-click / middle-click) counts as an
            // access — record the time even though the tab isn't active yet.
            const isNewTab = chromeTab.id != null && newTabIds.has(chromeTab.id);
            if (isNewTab && changeInfo.url) {
                newTabIds.delete(chromeTab.id!);
            }

            await ensurePageTracked(
                nurl,
                // Update access time when:
                // (a) URL changed in an active tab (fresh in-tab navigation, covers the case where
                //     handleTabActivated fired before the URL was set), OR
                // (b) URL is appearing for the first time on a brand-new tab (background open).
                !!(changeInfo.url && (chromeTab.active || isNewTab)),
                effectiveTitle(nurl.normalized, chromeTab.title),
                chromeTab.favIconUrl,
                chromeTab.windowId
            );
            dbLog('updated', chromeTab);
            await updateOpenerLinks(chromeTab);
            // Only compute adjacent-access links when the URL actually changed in this tab
            // (i.e. the user navigated within the same focused tab by clicking a link).
            // changeInfo.url is only present on that one event — not on the many intermediate
            // onUpdated events for status, title, or favicon changes.
            if (changeInfo.url) {
                await updateAdjacentAccessLinks(chromeTab);
            }
        }
    } catch(e) {
        logger.error("handleTabUpdated error", e);
    }
}

export async function syncInitialState() {
   if (!chrome || !chrome.windows) return;
   
   isInitializing = true;
   try {
       logger.info('[DB Sync] Starting syncInitialState. Getting all windows...');
       const windows = await chrome.windows.getAll({ populate: true });
       windows.forEach(w => w.tabs?.forEach(t => { if (t.id != null) restoredTabIds.add(t.id); }));
       logger.info(`[DB Sync] Fetched ${windows.length} windows from Chrome. Updating from browser...`);
       await updateFromBrowser(windows);
       logger.info('[DB Sync] Completed syncInitialState successfully.');
   } catch(e: any) {
       logger.error("[DB Sync] FATAL ERROR during syncInitialState:", e);
   } finally {
       isInitializing = false;
   }
}

export function registerDBSyncListeners() {
    chrome.tabs.onCreated.addListener(handleTabCreated);
    chrome.tabs.onActivated.addListener(handleTabActivated);
    chrome.tabs.onUpdated.addListener(handleTabUpdated);
    chrome.tabs.onRemoved.addListener((tabId) => { newTabIds.delete(tabId); });
}
